import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import {
  Search as SearchIcon,
  Bloodtype as GlucoseIcon,
  MonitorWeight as WeightIcon,
  Favorite as BPIcon,
} from "@mui/icons-material";
import { Layout } from "../components/Layout";
import { patientService, Patient } from "../services/patient.service";
import { formatLocalDate } from "../utils/dateUtils";

const PatientsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleSearch = async () => {
    setLoading(true);
    setError("");

    try {
      const result = await patientService.searchPatients(searchTerm);
      setPatients(result.patients);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to search patients");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Aggregate program enrollments across all providers
  const getProgramEnrollments = (patient: Patient) => {
    if (!patient.associations || patient.associations.length === 0) {
      return { diabetes: false, weight: false, hypertension: false };
    }

    return patient.associations.reduce(
      (acc, assoc) => ({
        diabetes: acc.diabetes || !!assoc.diabetesProgram,
        weight: acc.weight || !!assoc.weightProgram,
        hypertension: acc.hypertension || !!assoc.hypertensionProgram,
      }),
      { diabetes: false, weight: false, hypertension: false },
    );
  };

  return (
    <Layout>
      <Box>
        <Typography variant='h4' gutterBottom>
          Patient Management
        </Typography>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              fullWidth
              label='Search patients'
              placeholder='Enter name, chart ID, or external ID'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <Button
              variant='contained'
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={loading}
              sx={{ minWidth: 120 }}
            >
              Search
            </Button>
          </Box>
        </Paper>

        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display='flex' justifyContent='center' p={4}>
            <CircularProgress />
          </Box>
        ) : patients.length > 0 ? (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Date of Birth</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Program Enrollments</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {patients.map((patient) => {
                  const programs = getProgramEnrollments(patient);
                  return (
                    <TableRow key={patient.id} hover>
                      <TableCell>
                        <Typography variant='body2'>
                          {patient.lastname}, {patient.firstname}
                        </Typography>
                        <Typography
                          variant='caption'
                          color='text.secondary'
                          display='block'
                        >
                          ID: {patient.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {formatLocalDate(patient.dob) || "-"}
                      </TableCell>
                      <TableCell>
                        {patient.city}, {patient.state}
                      </TableCell>
                      <TableCell>
                        {patient.associations &&
                        patient.associations.length > 0 ? (
                          <Box>
                            {patient.associations.map((assoc, idx) => (
                              <Box
                                key={idx}
                                sx={{
                                  mb:
                                    idx < patient.associations!.length - 1
                                      ? 1
                                      : 0,
                                }}
                              >
                                <Typography variant='body2'>
                                  {assoc.entityName || "-"}
                                </Typography>
                                {assoc.externalLookup && (
                                  <Typography
                                    variant='caption'
                                    color='text.secondary'
                                    display='block'
                                  >
                                    External ID: {assoc.externalLookup}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        <Box display='flex' gap={0.5}>
                          {programs.diabetes && (
                            <Chip
                              icon={<GlucoseIcon />}
                              label='GL'
                              size='small'
                              sx={{
                                bgcolor: "#0393b8",
                                color: "white",
                                "& .MuiChip-icon": { color: "white" },
                              }}
                            />
                          )}
                          {programs.weight && (
                            <Chip
                              icon={<WeightIcon />}
                              label='WS'
                              size='small'
                              sx={{
                                bgcolor: "#569a45",
                                color: "white",
                                "& .MuiChip-icon": { color: "white" },
                              }}
                            />
                          )}
                          {programs.hypertension && (
                            <Chip
                              icon={<BPIcon />}
                              label='BP'
                              size='small'
                              sx={{
                                bgcolor: "#c73e2e",
                                color: "white",
                                "& .MuiChip-icon": { color: "white" },
                              }}
                            />
                          )}
                          {!programs.diabetes &&
                            !programs.weight &&
                            !programs.hypertension &&
                            "-"}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Button
                          size='small'
                          variant='outlined'
                          onClick={() => navigate(`/patients/${patient.id}`)}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <Typography color='text.secondary'>
              Enter a search term to find patients
            </Typography>
          </Paper>
        )}
      </Box>
    </Layout>
  );
};

export default PatientsPage;
